import { computed, ref, watch } from 'vue'
import { defineStore, storeToRefs } from 'pinia'
import { result as _result } from 'lodash-es'
import { useApi, isApiOk } from 'soykajs/composables/api'
import { useUserStore } from '@/stores/user'
import { i18n } from '@/i18n'

export const useConfigsStore = defineStore('configs', () => {
  const config = ref({})
  // Системные значения, которые должны совпадать с backend-константами справочников.
  const constants = {
    DROPDOWN_NA_KEY: 'n_a',
  }

  // Загружает dropdown config во временную модель, чтобы основной config обновлялся явно.
  // abortPrevious: false — очередь fill/fillEntity сериализует вызовы; abort ломал
  // status_composite (пустой Status в подачах).
  const { refresh: refreshConfig, model: scratchConfig } = useApi({
    url: 'config',
    data: {},
    pick: d => d,
  })
  const isCommonLoaded = ref(false)
  const commonLoading = ref(null)
  const loadedEntities = ref(new Set())
  const entityInflight = new Map()
  /** Сериализует GET /config*, иначе AbortController в useApi отменяет соседний fillEntity. */
  let configFetchQueue = Promise.resolve()
  const { isAuthorized } = storeToRefs(useUserStore())

  const readLoadedConfig = () => {
    const loaded = scratchConfig.value
    return loaded && typeof loaded === 'object' ? loaded : null
  }

  const get = (pth, def = null) => computed(() => _result({ ...config.value }, pth, def))

  const getById = (pth, id, def) =>
    computed(() => {
      const c = _result(config.value, pth, [])
      for (const dd of c) {
        if (dd.id == id) {
          return dd.value
        }
      }
      return def
    })

  function enqueueConfigFetch(task) {
    const run = configFetchQueue.then(task, task)
    configFetchQueue = run.catch(() => {})
    return run
  }

  /**
   * Однократно загружает common dropdowns.
   * Backend оставляет этот payload маленьким: GET /config возвращает только namespace "common".
   */
  const fill = async (force = false) => {
    if (isCommonLoaded.value && !force) {
      return
    }
    if (commonLoading.value && !force) {
      return commonLoading.value
    }
    commonLoading.value = enqueueConfigFetch(async () => {
      if (isCommonLoaded.value && !force) {
        return
      }
      await refreshConfig({ url: 'config' })
      const loaded = readLoadedConfig()
      // Abort/пустой ответ — не затираем уже загруженные entity (status_composite и т.п.).
      if (!loaded) {
        return
      }
      config.value = {
        ...config.value,
        ...loaded,
      }
      isCommonLoaded.value = true
    }).finally(() => {
      commonLoading.value = null
    })
    return commonLoading.value
  }

  /**
   * Загружает явный dropdown scope, например namespaces для products,
   * и мержит его с уже загруженными config-данными.
   *
   * Важно: не помечать entity загруженной, если запрос был abort'нут соседним
   * fillEntity — иначе status_composite/stage навсегда остаются пустыми до F5.
   */
  const fillEntity = async (entity, force = false) => {
    if (!entity || (loadedEntities.value.has(entity) && !force)) {
      return
    }
    if (!force && entityInflight.has(entity)) {
      return entityInflight.get(entity)
    }

    const pending = enqueueConfigFetch(async () => {
      if (loadedEntities.value.has(entity) && !force) {
        return
      }
      await refreshConfig({ url: `config/${entity}` })
      const loaded = readLoadedConfig()
      if (!loaded) {
        return
      }
      // Abort соседнего refresh оставляет в model чужой/старый payload —
      // не считаем entity загруженной, если в ответе нет её блока.
      if (entity !== 'all' && !(entity in loaded)) {
        return
      }
      config.value = {
        ...config.value,
        ...loaded,
      }
      loadedEntities.value.add(entity)
    }).finally(() => {
      entityInflight.delete(entity)
    })

    entityInflight.set(entity, pending)
    return pending
  }

  /**
   * Загружает полный экспорт dropdowns для страницы администрирования выпадающих списков.
   */
  const fillAll = async (force = false) => {
    await fillEntity('all', force)
  }

  watch(
    isApiOk,
    async (now, prev) => {
      if (now && !prev && isAuthorized.value) {
        await fill()
      }
    },
    { immediate: true }
  )

  // /config отдаёт подписи системных значений на языке X-Locale (URL от языка
  // не зависит) — при смене языка перечитываем загруженные блоки.
  // TODO: от языка зависят только отдельные поля config — перечитывать только их,
  // а не весь config.
  watch(
    () => i18n.global.locale.value,
    async (now, prev) => {
      if (!prev || now === prev || !isAuthorized.value) {
        return
      }
      const entities = [...loadedEntities.value]
      config.value = {}
      isCommonLoaded.value = false
      loadedEntities.value = new Set()
      await fill(true)
      await Promise.all(entities.map(entity => fillEntity(entity, true)))
    }
  )

  return {
    config,
    constants,
    get,
    getById,
    isApiOk,

    fill,
    fillEntity,
    fillAll,
  }
})
